import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getForecastDashboard } from '../api';
import LoadingState from './LoadingState';
import SegmentedControl from './ui/SegmentedControl';

/* ═══════════════════════════════════════════════════════════════════════════
   Forecast tab — warm-cream + sage redesign
   Single-file: helpers + icons + charts + regions + main component.
   Sibling of Performance / CE Activity / Rollup — token-only colors.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ─────────────── Formatters ─────────────── */

const fmt$ = (n, { abbrev = false, signed = false } = {}) => {
  if (n == null || Number.isNaN(+n)) return '—';
  const num = +n;
  const abs = Math.abs(num);
  if (abbrev && abs >= 1000) {
    const isMillions = abs >= 1_000_000;
    const div = isMillions ? 1_000_000 : 1_000;
    const suffix = isMillions ? 'M' : 'K';
    const value = (abs / div).toFixed(abs / div < 10 ? 1 : 0);
    return (num < 0 ? '-' : signed ? '+' : '') + '$' + value + suffix;
  }
  const s = abs.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (num < 0) return '-' + s;
  if (signed && num > 0) return '+' + s;
  return s;
};

const fmtPct = (n, dp = 1, { signed = false } = {}) => {
  if (n == null || Number.isNaN(+n)) return '—';
  const factor = Math.pow(10, dp);
  const rounded = Math.round(+n * factor) / factor;
  const s = rounded.toFixed(dp) + '%';
  if (signed && rounded > 0) return '+' + s;
  return s;
};

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const fmtMonth = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  return MONTHS_SHORT[m - 1] + " '" + String(y).slice(-2);
};

const fmtMonthLong = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  return MONTHS_LONG[m - 1] + ' ' + y;
};

const axisDollar = (v) => {
  if (v >= 1_000_000) return '$' + (v / 1_000_000).toFixed(v % 1_000_000 === 0 ? 0 : 1) + 'M';
  if (v >= 1000) return '$' + Math.round(v / 1000) + 'K';
  return '$' + Math.round(v);
};

/* Catmull-Rom → cubic Bézier smoothing for line/area paths. */
const smooth = (pts) => {
  if (pts.length < 2) return '';
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
  }
  return d;
};

/* ─────────────── Cost-category palette ─────────────── */

const CATEGORIES = [
  { key: 'labor', label: 'Labor', token: 'var(--cat-labor)', soft: 'var(--cat-labor-soft)', matches: ['labor'] },
  { key: 'material', label: 'Material', token: 'var(--cat-material)', soft: 'var(--cat-material-soft)', matches: ['material', 'materials'] },
  { key: 'equipment', label: 'Equipment', token: 'var(--cat-equipment)', soft: 'var(--cat-equipment-soft)', matches: ['equipment'] },
  { key: 'gc', label: 'General Conditions', token: 'var(--cat-gc)', soft: 'var(--cat-gc-soft)', matches: ['general conditions', 'gc', 'general'] },
  { key: 'other', label: 'Other', token: 'var(--cat-other)', soft: 'var(--cat-other-soft)', matches: ['other', 'misc', 'miscellaneous'] },
];

/* Server categories arrive as free-form strings ("Labor", "Material",
   "General Conditions", possibly anything). Bucket to internal keys; everything
   unknown falls into "other". */
const categoryKeyFor = (name) => {
  if (!name) return 'other';
  const n = String(name).toLowerCase().trim();
  for (const c of CATEGORIES) {
    if (c.matches.some((m) => n === m || n.includes(m))) return c.key;
  }
  return 'other';
};

/* ─────────────── Inline SVG icons (matches Icon.js vocabulary) ─────────────── */

const FIcon = ({ name, size = 14, sw = 1.6 }) => {
  const s = size;
  const wrap = (children) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block' }}
    >
      {children}
    </svg>
  );
  switch (name) {
    case 'refresh':
      return wrap(
        <>
          <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" />
          <path d="M21 3v5h-5" />
          <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" />
          <path d="M3 21v-5h5" />
        </>
      );
    case 'chart':
      return wrap(
        <>
          <path d="M4 20V10" />
          <path d="M10 20V4" />
          <path d="M16 20v-7" />
          <path d="M22 20H2" />
        </>
      );
    case 'arrow-r':
      return wrap(<path d="M5 12h14M13 6l6 6-6 6" />);
    case 'alert':
      return wrap(
        <>
          <path d="M12 9v4" />
          <path d="M12 17h.01" />
          <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
        </>
      );
    default:
      return null;
  }
};

/* ═══════════════════════════════════════════════════════════════════════════
   Chart primitives: dark tooltip + shared Y grid
   ═══════════════════════════════════════════════════════════════════════════ */

const DarkTooltip = ({ x, y, width, title, rows, flip = false }) => {
  const TW = 188;
  const left = Math.max(TW / 2 + 4, Math.min(width - TW / 2 - 4, x));
  return (
    <div
      style={{
        position: 'absolute',
        left,
        top: flip ? y + 14 : y - 12,
        transform: flip ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
        width: TW,
        pointerEvents: 'none',
        zIndex: 6,
        background: 'var(--text-1)',
        borderRadius: 9,
        boxShadow: '0 12px 28px -8px oklch(0.2 0.02 100 / 0.45)',
        padding: '11px 13px',
      }}
    >
      <div
        className="mono"
        style={{
          fontSize: 11.5,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'color-mix(in oklch, var(--bg-page) 62%, transparent)',
          marginBottom: 9,
        }}
      >
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {rows.map((r, i) => (
          <React.Fragment key={`${r.label}-${i}`}>
            {r.divider && (
              <div
                style={{
                  height: 1,
                  background: 'color-mix(in oklch, var(--bg-page) 22%, transparent)',
                  margin: '1px 0',
                }}
              />
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                {r.swatch && (
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: r.swatch,
                      flex: '0 0 auto',
                    }}
                  />
                )}
                <span
                  style={{
                    fontSize: 13,
                    color: 'color-mix(in oklch, var(--bg-page) 78%, transparent)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {r.label}
                </span>
              </div>
              <span
                className="mono"
                style={{
                  fontSize: 13.5,
                  fontWeight: r.bold ? 700 : 500,
                  color: r.color || 'var(--bg-page)',
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '-0.015em',
                  flex: '0 0 auto',
                }}
              >
                {r.value}
              </span>
            </div>
          </React.Fragment>
        ))}
      </div>
      <span
        style={{
          position: 'absolute',
          [flip ? 'top' : 'bottom']: -5,
          left: x - left + TW / 2,
          transform: 'translateX(-50%) rotate(45deg)',
          width: 9,
          height: 9,
          background: 'var(--text-1)',
          borderRadius: 1,
        }}
      />
    </div>
  );
};

const YGrid = ({ niceMax, x0, x1, TOP_PAD, CHART_H }) => {
  const ticks = [0, 1, 2, 3, 4];
  return (
    <g>
      {ticks.map((t) => {
        const frac = t / 4;
        const y = TOP_PAD + frac * CHART_H;
        const val = niceMax * (1 - frac);
        const isBase = t === 4;
        return (
          <g key={t}>
            <line
              x1={x0}
              x2={x1}
              y1={y}
              y2={y}
              stroke={isBase ? 'var(--border-default)' : 'var(--border-subtle)'}
              strokeWidth={1}
              opacity={isBase ? 1 : 0.4}
            />
            <text
              x={x0 - 10}
              y={y + 3.5}
              fontSize="10.5"
              fontFamily="var(--font-mono)"
              fill="var(--text-3)"
              textAnchor="end"
              style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}
            >
              {axisDollar(val)}
            </text>
          </g>
        );
      })}
    </g>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   CHART 1 — Monthly (paired Projected / Pending bars + sage glide area)
   ═══════════════════════════════════════════════════════════════════════════ */

const ForecastMonthlyChart = ({ months, hoveredMonth, setHoveredMonth }) => {
  const HEIGHT = 320;
  const TOP_PAD = 26;
  const BOTTOM_PAD = 34;
  const LEFT_AXIS = 58;
  const RIGHT_PAD = 18;
  const CHART_H = HEIGHT - TOP_PAD - BOTTOM_PAD;
  const COL = 86;
  const BAR_W = 24;
  const PAIR_GAP = 4;
  const width = Math.max(720, LEFT_AXIS + months.length * COL + RIGHT_PAD);
  const x1 = width - RIGHT_PAD;

  const maxVal = months.reduce((mx, m) => Math.max(mx, m.projected, m.pending), 0);
  const niceMax = maxVal * 1.15 || 100_000;
  const yFor = (v) => TOP_PAD + (1 - v / niceMax) * CHART_H;
  const baseY = TOP_PAD + CHART_H;

  const areaPts = months.map((m, i) => ({
    x: LEFT_AXIS + i * COL + COL / 2,
    y: yFor(m.projected),
  }));
  const areaLine = smooth(areaPts);
  const areaPath = areaPts.length
    ? `${areaLine} L ${areaPts[areaPts.length - 1].x} ${baseY} L ${areaPts[0].x} ${baseY} Z`
    : '';

  const hoveredIndex = months.findIndex((m) => m.month === hoveredMonth);
  const hovered = months[hoveredIndex];
  const tipRows = hovered
    ? [
        { label: 'Projected', value: fmt$(hovered.projected), swatch: 'var(--accent)' },
        ...(hovered.pending > 0
          ? [{ label: 'Pending Review', value: fmt$(hovered.pending), swatch: 'var(--warning)' }]
          : []),
        {
          label: 'Total billable',
          value: fmt$(hovered.projected + hovered.pending),
          bold: true,
          divider: true,
        },
      ]
    : [];
  const tipAnchorY = hovered ? yFor(Math.max(hovered.projected, hovered.pending)) : 0;
  const tipFlip = !!hovered && tipAnchorY - (44 + tipRows.length * 23) - 8 < TOP_PAD;

  return (
    <div style={{ position: 'relative', overflowX: 'auto' }}>
      <svg
        width={width}
        height={HEIGHT}
        style={{ display: 'block' }}
        onMouseLeave={() => setHoveredMonth(null)}
      >
        <defs>
          <linearGradient id="fcMonProj" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" />
            <stop offset="100%" stopColor="color-mix(in oklch, var(--accent) 45%, var(--accent-soft))" />
          </linearGradient>
          <linearGradient id="fcMonPend" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--warning)" />
            <stop offset="100%" stopColor="color-mix(in oklch, var(--warning) 45%, var(--warning-soft))" />
          </linearGradient>
          <linearGradient id="fcMonArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.14" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.02" />
          </linearGradient>
          <filter id="fcBarShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="oklch(0.2 0.02 135)" floodOpacity="0.1" />
          </filter>
        </defs>

        <YGrid niceMax={niceMax} x0={LEFT_AXIS} x1={x1} TOP_PAD={TOP_PAD} CHART_H={CHART_H} />

        <path d={areaPath} fill="url(#fcMonArea)" />
        <path d={areaLine} fill="none" stroke="var(--accent)" strokeWidth={1.5} opacity={0.35} />

        {hoveredIndex >= 0 && (
          <rect
            x={LEFT_AXIS + hoveredIndex * COL}
            y={TOP_PAD}
            width={COL}
            height={CHART_H}
            fill="color-mix(in oklch, var(--accent) 4%, transparent)"
            rx={6}
          />
        )}

        {months.map((m, i) => {
          const isHovered = m.month === hoveredMonth;
          const cx = LEFT_AXIS + i * COL + COL / 2;
          const pend = m.pending > 0;
          const groupW = pend ? BAR_W * 2 + PAIR_GAP : BAR_W;
          let cursorX = cx - groupW / 2;
          const projX = cursorX;
          cursorX += BAR_W + (pend ? PAIR_GAP : 0);
          const pendX = cursorX;
          const projY = yFor(m.projected);
          const projH = baseY - projY;
          const pendY = pend ? yFor(m.pending) : 0;
          const pendH = pend ? baseY - pendY : 0;
          const dim = hoveredMonth && !isHovered;
          return (
            <g
              key={m.month}
              onMouseEnter={() => setHoveredMonth(m.month)}
              style={{ cursor: 'default' }}
            >
              <rect
                x={LEFT_AXIS + i * COL}
                y={TOP_PAD}
                width={COL}
                height={CHART_H + BOTTOM_PAD}
                fill="transparent"
              />
              <rect
                x={projX}
                y={projY}
                width={BAR_W}
                height={Math.max(2, projH)}
                rx={4}
                fill="url(#fcMonProj)"
                opacity={dim ? 0.55 : 1}
                filter={isHovered ? 'url(#fcBarShadow)' : 'none'}
              />
              {pend && (
                <rect
                  x={pendX}
                  y={pendY}
                  width={BAR_W}
                  height={Math.max(2, pendH)}
                  rx={4}
                  fill="url(#fcMonPend)"
                  opacity={dim ? 0.55 : 1}
                  filter={isHovered ? 'url(#fcBarShadow)' : 'none'}
                />
              )}
              {isHovered && (
                <text
                  x={projX + BAR_W / 2}
                  y={projY - 7}
                  fontSize="10"
                  fontWeight="700"
                  fontFamily="var(--font-mono)"
                  fill="var(--accent)"
                  textAnchor="middle"
                  style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}
                >
                  {fmt$(m.projected, { abbrev: true })}
                </text>
              )}
              <text
                x={cx}
                y={HEIGHT - 12}
                fontSize="10"
                fontFamily="var(--font-sans)"
                fill={isHovered ? 'var(--text-1)' : 'var(--text-3)'}
                fontWeight={isHovered ? 700 : 500}
                textAnchor="middle"
                style={{ letterSpacing: '-0.005em' }}
              >
                {fmtMonth(m.month)}
              </text>
            </g>
          );
        })}
      </svg>

      {hovered && (
        <DarkTooltip
          x={LEFT_AXIS + hoveredIndex * COL + COL / 2}
          y={tipAnchorY}
          width={width}
          flip={tipFlip}
          title={fmtMonthLong(hovered.month)}
          rows={tipRows}
        />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   CHART 2 — By Category (stacked cost-category bars)
   ═══════════════════════════════════════════════════════════════════════════ */

const ForecastCategoryChart = ({ months, hoveredMonth, setHoveredMonth }) => {
  const HEIGHT = 320;
  const TOP_PAD = 26;
  const BOTTOM_PAD = 34;
  const LEFT_AXIS = 58;
  const RIGHT_PAD = 18;
  const CHART_H = HEIGHT - TOP_PAD - BOTTOM_PAD;
  const COL = 86;
  const BAR_W = 32;
  const width = Math.max(720, LEFT_AXIS + months.length * COL + RIGHT_PAD);
  const x1 = width - RIGHT_PAD;

  const maxVal = months.reduce((mx, m) => Math.max(mx, m.projected), 0);
  const niceMax = maxVal * 1.15 || 100_000;
  const yFor = (v) => TOP_PAD + (1 - v / niceMax) * CHART_H;
  const baseY = TOP_PAD + CHART_H;

  const presentCats = CATEGORIES.filter((c) =>
    months.some((m) => (m.breakdown[c.key] || 0) > 0)
  );
  const hoveredIndex = months.findIndex((m) => m.month === hoveredMonth);
  const hovered = months[hoveredIndex];
  const tipRows = hovered
    ? [
        ...presentCats
          .filter((c) => (hovered.breakdown[c.key] || 0) > 0)
          .map((c) => ({
            label: c.label,
            value: fmt$(hovered.breakdown[c.key]),
            swatch: c.token,
          })),
        { label: 'Total', value: fmt$(hovered.projected), bold: true, divider: true },
      ]
    : [];
  const tipAnchorY = hovered ? yFor(hovered.projected) : 0;
  const tipFlip = !!hovered && tipAnchorY - (44 + tipRows.length * 23) - 8 < TOP_PAD;

  return (
    <div style={{ position: 'relative', overflowX: 'auto' }}>
      <svg
        width={width}
        height={HEIGHT}
        style={{ display: 'block' }}
        onMouseLeave={() => setHoveredMonth(null)}
      >
        <defs>
          {CATEGORIES.map((c) => (
            <linearGradient key={c.key} id={`fcCat-${c.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={c.token} />
              <stop offset="100%" stopColor={c.soft} />
            </linearGradient>
          ))}
          <filter id="fcCatShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="oklch(0.2 0.02 135)" floodOpacity="0.1" />
          </filter>
        </defs>

        <YGrid niceMax={niceMax} x0={LEFT_AXIS} x1={x1} TOP_PAD={TOP_PAD} CHART_H={CHART_H} />

        {hoveredIndex >= 0 && (
          <rect
            x={LEFT_AXIS + hoveredIndex * COL}
            y={TOP_PAD}
            width={COL}
            height={CHART_H}
            fill="color-mix(in oklch, var(--accent) 4%, transparent)"
            rx={6}
          />
        )}

        {months.map((m, i) => {
          const isHovered = m.month === hoveredMonth;
          const cx = LEFT_AXIS + i * COL + COL / 2;
          const bx = cx - BAR_W / 2;
          const dim = hoveredMonth && !isHovered;
          let cursorY = baseY;
          const segs = presentCats
            .map((c) => {
              const val = m.breakdown[c.key] || 0;
              const h = (val / niceMax) * CHART_H;
              const y = cursorY - h;
              cursorY = y;
              return { c, val, y, h };
            })
            .filter((s) => s.val > 0);
          return (
            <g key={m.month} onMouseEnter={() => setHoveredMonth(m.month)}>
              <rect
                x={LEFT_AXIS + i * COL}
                y={TOP_PAD}
                width={COL}
                height={CHART_H + BOTTOM_PAD}
                fill="transparent"
              />
              <g filter={isHovered ? 'url(#fcCatShadow)' : 'none'} opacity={dim ? 0.55 : 1}>
                {segs.map((s) => (
                  <rect
                    key={s.c.key}
                    x={bx}
                    y={s.y}
                    width={BAR_W}
                    height={Math.max(1.5, s.h)}
                    rx={3}
                    fill={`url(#fcCat-${s.c.key})`}
                  />
                ))}
              </g>
              <text
                x={cx}
                y={HEIGHT - 12}
                fontSize="10"
                fontFamily="var(--font-sans)"
                fill={isHovered ? 'var(--text-1)' : 'var(--text-3)'}
                fontWeight={isHovered ? 700 : 500}
                textAnchor="middle"
                style={{ letterSpacing: '-0.005em' }}
              >
                {fmtMonth(m.month)}
              </text>
            </g>
          );
        })}
      </svg>

      {hovered && (
        <DarkTooltip
          x={LEFT_AXIS + hoveredIndex * COL + COL / 2}
          y={tipAnchorY}
          width={width}
          flip={tipFlip}
          title={fmtMonthLong(hovered.month)}
          rows={tipRows}
        />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   CHART 3 — Burn-Down (remaining contract value over time)
   ═══════════════════════════════════════════════════════════════════════════ */

const ForecastBurnDownChart = ({ months, totalContract, hoveredMonth, setHoveredMonth }) => {
  const HEIGHT = 320;
  const TOP_PAD = 26;
  const BOTTOM_PAD = 34;
  const LEFT_AXIS = 58;
  const RIGHT_PAD = 72;
  const CHART_H = HEIGHT - TOP_PAD - BOTTOM_PAD;
  const width = Math.max(720, LEFT_AXIS + months.length * 86 + RIGHT_PAD);
  const x1 = width - RIGHT_PAD;
  const plotW = x1 - LEFT_AXIS;

  const niceMax = (totalContract > 0 ? totalContract : 1) * 1.1;
  const yFor = (v) => TOP_PAD + (1 - v / niceMax) * CHART_H;
  const xFor = (i) =>
    LEFT_AXIS + (months.length === 1 ? plotW / 2 : (i / (months.length - 1)) * plotW);
  const baseY = TOP_PAD + CHART_H;

  const pts = months.map((m, i) => ({ x: xFor(i), y: yFor(m.remainingEnd) }));
  const line = smooth(pts);
  const area = pts.length
    ? `${line} L ${pts[pts.length - 1].x} ${baseY} L ${pts[0].x} ${baseY} Z`
    : '';

  const dense = months.length > 14;
  const showLabel = (i) => !dense || i % 2 === 0 || i === months.length - 1;

  const contractY = yFor(totalContract);
  const hoveredIndex = months.findIndex((m) => m.month === hoveredMonth);
  const hovered = months[hoveredIndex];
  const tipRows = hovered
    ? [
        {
          label: 'Projected Remaining',
          value: fmt$(hovered.remainingEnd),
          swatch: 'var(--accent)',
        },
        {
          label: 'Billed to Date',
          value: fmt$(totalContract - hovered.remainingEnd),
          swatch: 'color-mix(in oklch, var(--bg-page) 55%, transparent)',
        },
      ]
    : [];
  const tipAnchorY = hovered ? pts[hoveredIndex].y : 0;
  const tipFlip = !!hovered && tipAnchorY - (44 + tipRows.length * 23) - 8 < TOP_PAD;

  return (
    <div style={{ position: 'relative', overflowX: 'auto' }}>
      <svg
        width={width}
        height={HEIGHT}
        style={{ display: 'block' }}
        onMouseLeave={() => setHoveredMonth(null)}
      >
        <defs>
          <linearGradient id="fcBurnArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.12" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.03" />
          </linearGradient>
          <filter id="fcDotGlow" x="-120%" y="-120%" width="340%" height="340%">
            <feDropShadow dx="0" dy="0" stdDeviation="3" floodColor="var(--accent)" floodOpacity="0.5" />
          </filter>
        </defs>

        <YGrid niceMax={niceMax} x0={LEFT_AXIS} x1={x1} TOP_PAD={TOP_PAD} CHART_H={CHART_H} />

        <line
          x1={LEFT_AXIS}
          x2={x1}
          y1={contractY}
          y2={contractY}
          stroke="var(--danger)"
          strokeWidth={1.5}
          strokeDasharray="5 4"
          opacity={0.5}
        />
        <line
          x1={LEFT_AXIS}
          x2={x1}
          y1={baseY}
          y2={baseY}
          stroke="var(--success)"
          strokeWidth={1.5}
          strokeDasharray="5 4"
          opacity={0.7}
        />

        <path d={area} fill="url(#fcBurnArea)" />
        <path
          d={line}
          fill="none"
          stroke="var(--accent)"
          strokeWidth={2.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {hoveredIndex >= 0 && (
          <line
            x1={pts[hoveredIndex].x}
            x2={pts[hoveredIndex].x}
            y1={TOP_PAD}
            y2={baseY}
            stroke="var(--accent)"
            strokeWidth={1.5}
            strokeDasharray="4 4"
            opacity={0.12}
          />
        )}

        {months.map((m, i) => {
          const isHovered = m.month === hoveredMonth;
          return (
            <g key={m.month} onMouseEnter={() => setHoveredMonth(m.month)}>
              <rect
                x={pts[i].x - 43}
                y={TOP_PAD}
                width={86}
                height={CHART_H + BOTTOM_PAD}
                fill="transparent"
              />
              <circle
                cx={pts[i].x}
                cy={pts[i].y}
                r={isHovered ? 6 : 3.5}
                fill="var(--bg-surface)"
                stroke="var(--accent)"
                strokeWidth={isHovered ? 3 : 2}
                filter={isHovered ? 'url(#fcDotGlow)' : 'none'}
                style={{ transition: 'r 120ms ease' }}
              />
              {showLabel(i) && (
                <text
                  x={pts[i].x}
                  y={HEIGHT - 12}
                  fontSize="10"
                  fontFamily="var(--font-sans)"
                  fill={isHovered ? 'var(--text-1)' : 'var(--text-3)'}
                  fontWeight={isHovered ? 700 : 500}
                  textAnchor="middle"
                  style={{ letterSpacing: '-0.005em' }}
                >
                  {fmtMonth(m.month)}
                </text>
              )}
            </g>
          );
        })}

        <g>
          <rect x={x1 + 6} y={contractY - 9} width={58} height={18} rx={9} fill="var(--danger-soft)" />
          <text
            x={x1 + 35}
            y={contractY + 3.5}
            fontSize="10"
            fontWeight="600"
            fontFamily="var(--font-sans)"
            fill="var(--danger)"
            textAnchor="middle"
          >
            Contract
          </text>
        </g>
        <g>
          <rect x={x1 + 6} y={baseY - 9} width={58} height={18} rx={9} fill="var(--success-soft)" />
          <text
            x={x1 + 35}
            y={baseY + 3.5}
            fontSize="10"
            fontWeight="600"
            fontFamily="var(--font-sans)"
            fill="var(--success)"
            textAnchor="middle"
          >
            $0 Target
          </text>
        </g>
      </svg>

      {hovered && (
        <DarkTooltip
          x={pts[hoveredIndex].x}
          y={tipAnchorY}
          width={width}
          flip={tipFlip}
          title={fmtMonthLong(hovered.month)}
          rows={tipRows}
        />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   Section header — H1 + date-range subtitle + Refresh button
   ═══════════════════════════════════════════════════════════════════════════ */

const ForecastSectionHeader = ({ cd, onRefresh, refreshing = false }) => {
  const hasRange = cd && cd.projectStart && cd.projectEnd;
  return (
    <header style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '0 2px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 0 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 20.5,
            fontWeight: 600,
            color: 'var(--text-1)',
            letterSpacing: '-0.02em',
          }}
        >
          Billing Forecast
        </h1>
        {hasRange && (
          <p
            style={{
              margin: 0,
              fontSize: 14,
              color: 'var(--text-3)',
              letterSpacing: '-0.005em',
            }}
          >
            {fmtMonth(cd.projectStart)} — {fmtMonth(cd.projectEnd)}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        className="fc-refresh"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          height: 32,
          padding: '0 12px',
          borderRadius: 7,
          fontSize: 14,
          fontWeight: 500,
          color: 'var(--text-2)',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          boxShadow: 'var(--shadow-xs)',
          cursor: refreshing ? 'wait' : 'pointer',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            animation: refreshing ? 'fc-spin 0.7s linear infinite' : 'none',
          }}
        >
          <FIcon name="refresh" size={13} />
        </span>
        Refresh
      </button>
    </header>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   KPI row — two progress rings + 4 supplementary stat cells
   ═══════════════════════════════════════════════════════════════════════════ */

const ProgressRing = ({ pct, tone, animKey }) => {
  const SIZE = 72;
  const STROKE = 7;
  const r = (SIZE - STROKE) / 2;
  const circ = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, pct));
  const targetOffset = circ * (1 - clamped / 100);
  const [offset, setOffset] = useState(circ);
  useEffect(() => {
    setOffset(circ);
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setOffset(targetOffset))
    );
    return () => cancelAnimationFrame(id);
  }, [targetOffset, circ, animKey]);
  return (
    <div style={{ position: 'relative', width: SIZE, height: SIZE }}>
      <svg
        width={SIZE}
        height={SIZE}
        style={{ display: 'block', transform: 'rotate(-90deg)' }}
      >
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={r}
          fill="none"
          stroke="color-mix(in oklch, var(--text-1) 8%, transparent)"
          strokeWidth={STROKE}
        />
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={r}
          fill="none"
          stroke={tone}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 600ms cubic-bezier(.4,0,.2,1)' }}
        />
      </svg>
      <div
        className="mono"
        style={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          placeItems: 'center',
          fontSize: 18.5,
          fontWeight: 700,
          color: tone,
          letterSpacing: '-0.02em',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {Math.round(clamped)}%
      </div>
    </div>
  );
};

const RingBlock = ({ pct, tone, label, value, sub, annotation, animKey }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 10,
      minWidth: 116,
    }}
  >
    <ProgressRing pct={pct} tone={tone} animKey={animKey} />
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontSize: 12.5,
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--text-3)',
        }}
      >
        {label}
      </div>
      <div
        className="mono"
        style={{
          fontSize: 19.5,
          fontWeight: 800,
          color: 'var(--text-1)',
          letterSpacing: '-0.02em',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        className="mono"
        style={{ fontSize: 12.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}
      >
        {sub}
      </div>
      {annotation && (
        <div
          className="mono"
          style={{
            fontSize: 12.5,
            color: 'var(--warning)',
            fontVariantNumeric: 'tabular-nums',
            marginTop: 1,
          }}
        >
          {annotation}
        </div>
      )}
    </div>
  </div>
);

const StatCell = ({ label, value, valueColor, sub, empty = false }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      minWidth: 140,
      flex: '1 1 140px',
    }}
  >
    <div
      style={{
        fontSize: 11.5,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--text-3)',
      }}
    >
      {label}
    </div>
    <div
      className="mono"
      style={{
        fontSize: 21.5,
        fontWeight: 800,
        color: empty ? 'var(--text-4)' : valueColor || 'var(--text-1)',
        letterSpacing: '-0.025em',
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1.15,
      }}
    >
      {value}
    </div>
    {sub && (
      <div
        className="mono"
        style={{ fontSize: 12.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}
      >
        {sub}
      </div>
    )}
  </div>
);

const Hairline = () => (
  <div
    style={{
      width: 1,
      height: 50,
      background: 'var(--border-default)',
      margin: '0 8px',
      flex: '0 0 auto',
      alignSelf: 'center',
    }}
  />
);

const ForecastKpiRow = ({ cd, animKey = 0 }) => {
  const billedPct = cd.totalContract > 0 ? (cd.billedToDate / cd.totalContract) * 100 : 0;
  const remaining = cd.totalContract - cd.billedToDate;
  const remainingPct = 100 - billedPct;
  return (
    <section
      style={{
        display: 'flex',
        alignItems: 'stretch',
        flexWrap: 'wrap',
        rowGap: 18,
        padding: '24px 28px',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 12,
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 28, flex: '0 0 auto' }}>
        <RingBlock
          pct={billedPct}
          tone="var(--success)"
          label="Billed to date"
          value={fmt$(cd.billedToDate)}
          sub={`of ${fmt$(cd.totalContract)}`}
          annotation={cd.totalRetainage > 0 ? `+${fmt$(cd.totalRetainage)} retainage held` : null}
          animKey={animKey}
        />
        <RingBlock
          pct={remainingPct}
          tone="var(--accent)"
          label="Remaining"
          value={fmt$(remaining)}
          sub={`${fmtPct(remainingPct, 1)} of contract`}
          animKey={animKey}
        />
      </div>
      <Hairline />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          rowGap: 18,
          columnGap: 0,
          flex: '1 1 560px',
        }}
      >
        <StatCell
          label="Pending Review"
          value={fmt$(cd.totalPending)}
          valueColor="var(--warning)"
          empty={!cd.totalPending}
          sub={cd.totalPending > 0 ? 'awaiting approval' : 'nothing pending'}
        />
        <Hairline />
        <StatCell
          label="Average per Month"
          value={fmt$(cd.avgPerMonth)}
          valueColor="var(--accent)"
          empty={!cd.avgPerMonth}
          sub={`across ${cd.months.length} months`}
        />
        {cd.highestMonth && (
          <>
            <Hairline />
            <StatCell
              label="Highest Forecast Month"
              value={fmt$(cd.highestMonth.value)}
              valueColor="var(--success)"
              sub={fmtMonth(cd.highestMonth.month)}
            />
          </>
        )}
        {cd.lowestMonth && (
          <>
            <Hairline />
            <StatCell
              label="Lowest Forecast Month"
              value={fmt$(cd.lowestMonth.value)}
              valueColor="var(--text-1)"
              sub={fmtMonth(cd.lowestMonth.month)}
            />
          </>
        )}
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   Chart card chrome + legend
   ═══════════════════════════════════════════════════════════════════════════ */

const ForecastLegend = ({ items }) => (
  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 14 }}>
    {items.map((it) => (
      <div key={it.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {it.line ? (
          <span
            style={{
              width: 20,
              height: 0,
              borderTop: `2px ${it.dash ? 'dashed' : 'solid'} ${it.color}`,
              display: 'inline-block',
            }}
          />
        ) : (
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 3,
              background: it.color,
              display: 'inline-block',
            }}
          />
        )}
        <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{it.label}</span>
      </div>
    ))}
  </div>
);

const ForecastChartCard = ({ title, legend, children }) => (
  <section
    style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 12,
      boxShadow: 'var(--shadow-sm)',
      padding: '20px 24px 24px',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        marginBottom: 16,
      }}
    >
      <h3
        style={{
          margin: 0,
          fontSize: 16.5,
          fontWeight: 700,
          color: 'var(--text-1)',
          letterSpacing: '-0.01em',
        }}
      >
        {title}
      </h3>
      {legend}
    </div>
    {children}
  </section>
);

/* ═══════════════════════════════════════════════════════════════════════════
   Empty + error states
   ═══════════════════════════════════════════════════════════════════════════ */

const ForecastEmptyState = () => (
  <section
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      padding: '56px 24px',
      textAlign: 'center',
      background: 'var(--bg-surface)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 12,
      boxShadow: 'var(--shadow-sm)',
    }}
  >
    <div
      style={{
        width: 56,
        height: 56,
        borderRadius: 14,
        background: 'var(--accent-soft)',
        border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)',
        color: 'var(--accent-text)',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <FIcon name="chart" size={24} sw={1.8} />
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 380 }}>
      <h3
        style={{
          margin: 0,
          fontSize: 17.5,
          fontWeight: 700,
          color: 'var(--text-1)',
          letterSpacing: '-0.01em',
        }}
      >
        No forecast data yet
      </h3>
      <p style={{ margin: 0, fontSize: 14.5, color: 'var(--text-3)', lineHeight: 1.7 }}>
        Forecast data is auto-generated from your task schedule and SOV allocations. Make sure
        tasks have start and end dates.
      </p>
    </div>
  </section>
);

const ForecastErrorState = ({ onRetry }) => (
  <section
    style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 12,
      overflow: 'hidden',
      boxShadow: 'inset 3px 0 0 var(--danger), var(--shadow-sm)',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '18px 20px' }}>
      <div style={{ color: 'var(--danger)', marginTop: 1, flex: '0 0 auto' }}>
        <FIcon name="alert" size={18} sw={1.8} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-1)' }}>
          Couldn’t load the forecast
        </div>
        <div style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.55 }}>
          Something went wrong generating this project’s billing forecast.{' '}
          <button
            type="button"
            onClick={onRetry}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--accent-text)',
              fontWeight: 600,
              textDecoration: 'underline',
              textUnderlineOffset: 2,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

const VIEW_TITLE = {
  monthly: 'Monthly Billing Forecast',
  category: 'Forecast by Cost Category',
  scurve: 'Remaining Contract Burn-Down',
};

const buildDashboardModel = (raw) => {
  if (!raw) return null;
  const {
    monthlyForecast = {},
    monthlyActuals = {},
    monthlyPending = {},
    categoryBreakdown = {},
    totalContract = 0,
    totalRetainage = 0,
    projectDates = {},
  } = raw;

  const ymSet = new Set([
    ...Object.keys(monthlyForecast),
    ...Object.keys(monthlyActuals),
    ...Object.keys(monthlyPending),
    ...Object.keys(categoryBreakdown),
  ]);
  const ymList = [...ymSet].sort();

  // billedToDate = sum of all actuals across all months
  const billedToDate = Object.values(monthlyActuals).reduce((s, v) => s + (+v || 0), 0);

  // Walk months forward to compute remainingEnd burn-down line.
  let remaining = totalContract - billedToDate;
  const months = ymList.map((ym) => {
    const projected = +monthlyForecast[ym] || 0;
    const pending = +monthlyPending[ym] || 0;
    const breakdown = { labor: 0, material: 0, equipment: 0, gc: 0, other: 0 };
    const rawBreakdown = categoryBreakdown[ym] || {};
    Object.entries(rawBreakdown).forEach(([catName, amt]) => {
      const key = categoryKeyFor(catName);
      breakdown[key] += +amt || 0;
    });
    const remainingStart = remaining;
    remaining -= projected;
    return {
      month: ym,
      projected,
      pending,
      breakdown,
      remainingStart,
      remainingEnd: Math.max(0, remaining),
    };
  });

  const projectedMonths = months.filter((m) => m.projected > 0);
  const totalProjected = months.reduce((s, m) => s + m.projected, 0);
  const totalPending = months.reduce((s, m) => s + m.pending, 0);
  const avgPerMonth = months.length > 0 ? Math.round(totalProjected / months.length) : 0;
  const highest = projectedMonths.length
    ? projectedMonths.reduce((best, m) => (m.projected > best.projected ? m : best))
    : null;
  const lowest = projectedMonths.length
    ? projectedMonths.reduce((worst, m) => (m.projected < worst.projected ? m : worst))
    : null;

  const projectStart = projectDates.start ? projectDates.start.slice(0, 7) : months[0]?.month || null;
  const projectEnd = projectDates.end
    ? projectDates.end.slice(0, 7)
    : months[months.length - 1]?.month || null;

  return {
    months,
    totalContract,
    totalRetainage,
    billedToDate,
    totalProjected,
    totalPending,
    avgPerMonth,
    highestMonth: highest ? { value: highest.projected, month: highest.month } : null,
    lowestMonth: lowest ? { value: lowest.projected, month: lowest.month } : null,
    projectStart,
    projectEnd,
  };
};

const ForecastTab = ({ projectId, refreshSummary }) => {
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('monthly');
  const [hoveredMonth, setHoveredMonth] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [animKey, setAnimKey] = useState(0);

  const fetchAll = useCallback(
    async ({ silent = false } = {}) => {
      if (!projectId) return;
      try {
        if (!silent) setLoading(true);
        else setRefreshing(true);
        const d = await getForecastDashboard(projectId);
        setRaw(d);
        setError(null);
        setAnimKey((k) => k + 1);
      } catch (err) {
        console.error(err);
        setError('Failed to load forecast data');
      } finally {
        setLoading(false);
        // give the ring re-animation a beat before flipping the spinner off
        setTimeout(() => setRefreshing(false), 750);
      }
    },
    [projectId]
  );

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, [projectId]);

  const onRefresh = useCallback(() => {
    fetchAll({ silent: true });
    if (typeof refreshSummary === 'function') {
      try {
        refreshSummary();
      } catch {
        // best-effort; parent summary is decorative here
      }
    }
  }, [fetchAll, refreshSummary]);

  const cd = useMemo(() => buildDashboardModel(raw), [raw]);

  // Reset tooltip pin when the user switches sub-view
  const onChangeView = useCallback((next) => {
    setView(next);
    setHoveredMonth(null);
  }, []);

  const legendFor = (v, model) => {
    if (!model) return null;
    if (v === 'monthly') {
      const items = [{ label: 'Projected', color: 'var(--accent)' }];
      if (model.months.some((m) => m.pending > 0)) {
        items.push({ label: 'Pending Review', color: 'var(--warning)' });
      }
      return <ForecastLegend items={items} />;
    }
    if (v === 'category') {
      const present = CATEGORIES.filter((c) =>
        model.months.some((m) => (m.breakdown[c.key] || 0) > 0)
      );
      return <ForecastLegend items={present.map((c) => ({ label: c.label, color: c.token }))} />;
    }
    return (
      <ForecastLegend
        items={[
          { label: 'Projected Remaining', color: 'var(--accent)', line: true },
          { label: 'Contract', color: 'var(--danger)', line: true, dash: true },
          { label: '$0 Target', color: 'var(--success)', line: true, dash: true },
        ]}
      />
    );
  };

  if (loading) return <LoadingState message="Generating forecast…" padding="80px 20px" />;

  const hasData = cd && cd.months.length > 0;
  const segOptions = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'category', label: 'By Category' },
    { value: 'scurve', label: 'Burn-Down' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`
        @keyframes fc-spin { to { transform: rotate(360deg); } }
        .fc-refresh:hover:not(:disabled) {
          border-color: color-mix(in oklch, var(--accent) 35%, transparent) !important;
          color: var(--accent-text) !important;
          background: var(--accent-soft) !important;
        }
      `}</style>

      <ForecastSectionHeader cd={cd || {}} onRefresh={onRefresh} refreshing={refreshing} />

      {error ? (
        <ForecastErrorState onRetry={() => fetchAll()} />
      ) : !hasData ? (
        <ForecastEmptyState />
      ) : (
        <>
          <ForecastKpiRow cd={cd} animKey={animKey} />

          <SegmentedControl value={view} onChange={onChangeView} options={segOptions} />

          <ForecastChartCard title={VIEW_TITLE[view]} legend={legendFor(view, cd)}>
            {view === 'monthly' && (
              <ForecastMonthlyChart
                months={cd.months}
                hoveredMonth={hoveredMonth}
                setHoveredMonth={setHoveredMonth}
              />
            )}
            {view === 'category' && (
              <ForecastCategoryChart
                months={cd.months}
                hoveredMonth={hoveredMonth}
                setHoveredMonth={setHoveredMonth}
              />
            )}
            {view === 'scurve' && (
              <ForecastBurnDownChart
                months={cd.months}
                totalContract={cd.totalContract}
                hoveredMonth={hoveredMonth}
                setHoveredMonth={setHoveredMonth}
              />
            )}
          </ForecastChartCard>
        </>
      )}
    </div>
  );
};

export default ForecastTab;
